import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, CardProjectSummary, CardProjectSummaryTask, TaskTypeIcon } from '@spark';
import { GlobalContext } from '@/app/providers/globalState';
import { useAuth } from '@/features/auth/AuthContext';
import { getRecentProjectTasks } from '@/features/tasks/utils/taskTypeIcons';
import { buildTaskNavigation } from '@/features/tasks/utils/navigation';
import { getTaskDraft, draftWizardUrl } from '@/features/tasks/utils/taskDrafts';
import { DeleteProjectModal } from './DeleteProjectModal.jsx';
import { ProjectOverflowMenu } from './ProjectOverflowMenu.jsx';
import { resolveProjectDeleteScope } from '@/features/tasks/utils/projectDeleteScope';
import { handleCardSelectionClick } from '@/features/tasks/utils/cardSelectionClick';

const EMPTY_PROJECT_GOAL = 'No project goal provided.';

const moreTasksLabel = (count) => `+${count} ${count === 1 ? 'task' : 'tasks'}`;

/**
 * Project summary card shown on the all-projects level of the cards view.
 * Clicking the card, empty space beside a previewed task, or the "+N tasks"
 * control drills into the project; clicking a previewed task's icon or name
 * opens that task. The dots menu offers edit/delete.
 * Figma: Projects `8007:4572`.
 */
export const ProjectCard = ({
    app,
    allApps,
    onOpen,
    onEdit,
    onDelete,
    selected = false,
    selectionKey,
    onToggleSelect,
    onRangeSelect,
    shouldSuppressClick,
}) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const navigate = useNavigate();
    const { setCurrentUsecaseId, evaluationResultsReady } = useContext(GlobalContext);
    const { role } = useAuth();
    const { preview, remainingCount, totalCount } = getRecentProjectTasks(app);
    const canManage = role !== 'Viewer';
    const deleteScope = resolveProjectDeleteScope(app, allApps);
    const isFilteredDelete = deleteScope.mode === 'filtered-tasks';
    const selectionArgs = {
        selectionKey,
        shouldSuppressClick,
        onToggleSelect,
        onRangeSelect,
    };

    const openProject = (e) => {
        if (handleCardSelectionClick(e, selectionArgs)) return;
        onOpen?.(app.group_name);
    };

    const openPreviewTask = (e, subGroup) => {
        e.stopPropagation();
        if (handleCardSelectionClick(e, selectionArgs)) return;
        const usecases = subGroup?.usecases || [];
        if (usecases.length === 0) return;
        const draftUsecase = usecases.find((uc) => getTaskDraft(uc)) || null;
        const draft = draftUsecase ? getTaskDraft(draftUsecase) : null;
        if (draft) {
            setCurrentUsecaseId(draftUsecase._id);
            navigate(draftWizardUrl(draftUsecase._id, draft.step));
            return;
        }
        const nav = buildTaskNavigation(usecases[0], usecases, { evaluationResultsReady });
        if (!nav) return;
        if (nav.usecaseId) setCurrentUsecaseId(nav.usecaseId);
        navigate(nav.url, nav.state ? { state: nav.state } : undefined);
    };

    const handleDeleteConfirm = async () => {
        if (!onDelete) return;
        setDeleteLoading(true);
        try {
            await onDelete(app);
            setShowDeleteModal(false);
        } catch (error) {
            console.error('Error deleting project:', error);
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <>
            <CardProjectSummary
                className="fs-unmask"
                selected={selected}
                title={`${app.group_name} (${totalCount})`}
                description={(app.project_goal || '').trim() || EMPTY_PROJECT_GOAL}
                moreActions={
                    canManage ? (
                        <ProjectOverflowMenu
                            deleteLabel={isFilteredDelete ? 'Delete filtered tasks' : 'Delete Project'}
                            onEdit={() => onEdit && onEdit(app)}
                            onDelete={() => setShowDeleteModal(true)}
                        />
                    ) : undefined
                }
                moreTasks={
                    remainingCount > 0 ? (
                        <Button
                            variant="ghost"
                            size="tiny"
                            className="fs-unmask"
                            onClick={(e) => {
                                e.stopPropagation();
                                openProject(e);
                            }}
                        >
                            {moreTasksLabel(remainingCount)}
                        </Button>
                    ) : undefined
                }
                onClick={(e) => {
                    if (e.target.closest?.('button') || e.target.closest?.('[role="menu"]')) return;
                    openProject(e);
                }}
            >
                {preview.map((task) => (
                    <CardProjectSummaryTask
                        key={task.key}
                        icon={<TaskTypeIcon type={task.iconType} size={32} agentic={task.agentic} />}
                        onClick={(e) => openPreviewTask(e, task.subGroup)}
                    >
                        {task.name}
                    </CardProjectSummaryTask>
                ))}
            </CardProjectSummary>

            <DeleteProjectModal
                visible={showDeleteModal}
                onCancel={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteConfirm}
                projectName={app.group_name}
                loading={deleteLoading}
                isFilteredDelete={isFilteredDelete}
                filteredTaskCount={deleteScope.visibleTaskCount}
            />
        </>
    );
};

export default ProjectCard;
